import { Checkbox } from "@fluentui/react";
import { FieldArray } from "formik";
import React from "react";
import { productsCreate, productsUpdate } from "../../../api/api";
import { useProducts } from "../../../state/api/products/useProducts";
import { useGhlTags } from "../../../state/api/services/ghl/useTags";
import { Doc } from "../../../types/db/collections";
import { Product } from "../../../types/db/schema/products/product.schema";
import Button, { ButtonBase } from "../buttons/Button";
import Submit from "../buttons/Submit";
import SearchableComboBox from "../combobox/SearchableComboBox";
import FieldWithLabel from "../formik/FieldWithLabel";
import Icon from "../icons/Icon";
import { Dialog, DialogBody, DialogFooter, ModalForm } from "../modal/ModalForm";
import AsAdmin from "../session/AsAdmin";
import GhlTag from "../tags/GhlTag";

interface Props extends ButtonBase {
  product?: Doc<Product>
}
export default function ProductDialog(props: Props) {
  return (
    <AsAdmin>
      <Dialog
        trigger={(
          <Button
            variant={props.product ? "subtle" : undefined}
            text={props.product ? "Edit" : "Create Product"}
            icon={props.product ? "pen" : "plus"}
            {...props}
          />
        )}
        dialog={<ProductDialogInner {...props} />}
      />
    </AsAdmin>
  )
}

function ProductDialogInner(props: Props) {

  const {
    product
  } = props;

  const { tags, tagsById, isLoading: loadingTags } = useGhlTags();
  const { mutate } = useProducts({ enabled: false });

  return (
    <ModalForm
      title={product ? "Edit Product" : "Create Product"}
      description={product ? `Edit the "${product.name}" product category` : "Create a new product category"}
      initialValues={{
        name: "",
        description: "",
        isActive: true,
        shortcode: "",
        order: 0,
        tabColor: "#000000",
        requiredTags: [],
        ...product
      } as Product}
      onSubmit={async (values, callApi) => {

        const res = await callApi(
          product?._id
            ? productsUpdate(values as Doc<Product>)
            : productsCreate(values)
        );

        if (!res?.success) {
          return false;
        }

        await mutate();
        return true;
      }}
    >
      {
        ({ values, setFieldValue }) => (
          <>
            <DialogBody>
              <div className="d-flex flex-row gap-3 w-100">
                <FieldWithLabel className="w-100" name="name" label="Product Name" />
                <FieldWithLabel className="w-100" name="shortcode" label="Short Label" />
              </div>
              <div className="d-flex flex-row gap-3 w-100">
                <FieldWithLabel className="w-100" name="unlockLink" label="URL for purchase" />
                <FieldWithLabel type="number" className="w-100" name="order" label="Order (in Product List)" />
              </div>
              <FieldWithLabel as="textarea" name="description" label="Description" />
              <FieldArray name="requiredTags">
                {
                  ({ push, remove }) => (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                        <label>Access Tags</label>
                        <SearchableComboBox
                          loading={loadingTags}
                          values={tags || []}
                          valuesById={tagsById}
                          filterPlaceholder="Search Tags..."
                          placeholder="Add tags"
                          itemToId={t => t.id}
                          itemToString={t => t.name}
                          onChange={t => push(t.id)}
                          value={values.requiredTags}
                          allowMultiple
                          cannotClearValue
                          preventChangeOnSelectedClick
                          keepOpen
                          alwaysShowPlaceholder
                        />
                      </div>
                      <div className="d-flex flex-row flex-wrap gap-2">
                        {
                          !!values.requiredTags?.length
                            ? (
                              values.requiredTags.map((f: string, i: number) => (
                                <div className="fs-5 d-flex flex-row gap-2 align-items-center" key={f}>
                                  <GhlTag id={f} />
                                  <Icon
                                    icon="trash"
                                    className="text-danger"
                                    onClick={() => remove(i)}
                                  />
                                </div>
                              ))
                            )
                            : (
                              <strong style={{ fontSize: "10.5px" }}>
                                Product is available to everyone
                              </strong>
                            )
                        }
                      </div>
                    </div>
                  )
                }
              </FieldArray>
              <div className="d-flex flex-column gap-1 w-100">
                <label>Tab Color</label>
                <input type="color" className="form-control form-control-color p-0 border-0" value={values.tabColor} onChange={e => setFieldValue("tabColor", e.target.value)} />
              </div>
              <div className="d-flex flex-column w-100 gap-1">
                <label>Status</label>
                <Checkbox
                  styles={{ checkbox: { height: "16px", width: "16px" } }}
                  onRenderLabel={() => <strong className="ms-1" style={{ fontSize: "11.5px", color: "var(--gray-900)" }}>Active - visible to users</strong>}
                  checked={values.isActive}
                  onChange={(_, v) => setFieldValue("isActive", v)}
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Submit />
            </DialogFooter>
          </>
        )
      }
    </ModalForm >
  )
}